import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Card, Stepper, Frame, IconChip,
} from '../../../components/onboarding/primitives';
import { paymentOrdersAPI } from '../../../services/api';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع'];

/**
 * Props contract: { state, setState, goToStep, goBack }
 *
 * Reads:
 *   state.s4.planDoc   — full Plan object (stashed by Step4Plan), needed for
 *                        plan.limits.maxDevices and plan.limits.additionalDevicePrice.
 *   state.s3.branches  — to re-submit branchCount to updateDraft.
 *   state.activeOrder  — optional; used to resume the previously-chosen count.
 *
 * Writes:
 *   state.s5.deviceCount  — the total (included + extra) the user confirmed.
 *   state.activeOrder     — refreshed with the order returned from updateDraft.
 *
 * API:
 *   paymentOrdersAPI.updateDraft({ planId, billingCycle, branchCount, deviceCount })
 *     → recomputes the full price breakdown on the server and flips
 *       deviceCountConfirmed=true so getStatus routes the user to Checkout.
 */
export default function Step5Devices({ state, setState, goToStep, goBack }) {
  // The plan blob has three possible sources, in order of preference:
  //   1. `state.s4.planDoc` — stashed by Step4Plan when the user clicked Next.
  //   2. `state.activeOrder.planId` (populated) — available after a server
  //      round-trip; may be a bare ObjectId if we got here via getStatus
  //      resume without a priorStep4 run.
  //   3. `fetchedPlan` — freshly fetched via getMyActive() when neither
  //      of the above has limits.maxDevices (older getPublicPlans responses
  //      stripped this field, so state.s4.planDoc can legitimately be
  //      missing it).
  const [fetchedPlan, setFetchedPlan] = useState(null);

  const stashedPlan = state?.s4?.planDoc;
  const orderPlan = (state?.activeOrder?.planId && typeof state.activeOrder.planId === 'object')
    ? state.activeOrder.planId
    : null;
  const plan = (fetchedPlan && fetchedPlan.limits?.maxDevices !== undefined)
    ? fetchedPlan
    : (stashedPlan && stashedPlan.limits?.maxDevices !== undefined)
      ? stashedPlan
      : (orderPlan || stashedPlan || {});

  const includedDevices = Math.max(1, plan?.limits?.maxDevices || 1);
  const devicePrice = Math.max(0, plan?.limits?.additionalDevicePrice || 2500);
  const cycle = state?.s4?.cycle || 'yearly';
  const branchCount = (state?.s3?.branches || []).length || 1;

  // Fetch the active draft when the in-state plan is missing the device
  // limits — this covers:
  //   • user resumed onboarding after closing the tab (no s4 state)
  //   • stale bundle deployed before the getPublicPlans fix was live
  useEffect(() => {
    const needsRefetch = !plan?.limits || plan.limits.maxDevices === undefined;
    if (!needsRefetch || fetchedPlan) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await paymentOrdersAPI.getMyActive();
        const order = res?.data?.data || res?.data;
        if (cancelled) return;
        if (order?.planId && typeof order.planId === 'object' && order.planId.limits) {
          setFetchedPlan(order.planId);
          // Also hydrate activeOrder on state so Step6 doesn't re-fetch.
          setState?.((prev) => ({ ...prev, activeOrder: { ...(prev?.activeOrder || {}), ...order } }));
        }
      } catch {
        // Silent — we'll use the conservative fallback (1 device) until
        // the user re-selects the plan.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialExtra = Math.max(
    0,
    (state?.activeOrder?.deviceCount || state?.s5?.deviceCount || includedDevices) - includedDevices,
  );
  const [extraDevices, setExtraDevices] = useState(initialExtra);
  const [saving, setSaving] = useState(false);

  // When limits arrive after first render, nudge extraDevices to reflect the
  // new baseline (otherwise we keep the 0 derived from a stale includedDevices=1).
  useEffect(() => {
    const ord = state?.activeOrder?.deviceCount;
    if (!ord) return;
    const nextExtra = Math.max(0, ord - includedDevices);
    setExtraDevices((cur) => (cur === 0 && nextExtra > 0 ? nextExtra : cur));
  }, [includedDevices, state?.activeOrder?.deviceCount]);

  const totalDevices = includedDevices + extraDevices;
  const extraCost = extraDevices * devicePrice;
  const MAX_EXTRA = 99;

  const inc = () => setExtraDevices((n) => Math.min(MAX_EXTRA, n + 1));
  const dec = () => setExtraDevices((n) => Math.max(0, n - 1));

  const handleNext = async () => {
    if (!plan?._id) {
      toast.error('معرف الباقة مفقود — رجاءً أعد اختيار الباقة');
      goToStep(4);
      return;
    }
    setSaving(true);
    try {
      const res = await paymentOrdersAPI.updateDraft({
        planId: plan._id,
        billingCycle: cycle,
        branchCount,
        deviceCount: totalDevices,
      });
      const updated = res.data?.data || res.data || {};
      setState((prev) => ({
        ...prev,
        activeOrder: { ...(prev.activeOrder || {}), ...updated },
        s5: { deviceCount: totalDevices },
      }));
      goToStep(6);
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]?.msg
        || 'تعذّر حفظ عدد الأجهزة، حاول مرة أخرى';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onBack = () => { if (goBack) goBack(); else goToStep(4); };

  return (
    <Frame
      maxWidth={720}
      stepper={<Stepper steps={STEP_LABELS} current={4} />}
      footer={
        <>
          <Button variant="outline" onClick={onBack} iconLeft={<Icon.Arrow dir="right" />}>السابق</Button>
          <Button
            onClick={handleNext}
            disabled={saving}
            icon={saving ? <Icon.Clock size={16} /> : <Icon.Arrow />}
          >
            {saving ? 'جارٍ الحفظ...' : 'التالي'}
          </Button>
        </>
      }
    >
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5 }}>
          الأجهزة الطبية
        </h1>
        <p style={{ fontSize: 14.5, color: BRAND.muted, margin: 0, lineHeight: 1.7 }}>
          كم جهاز طبي تحتاج أن تربطه بالنظام؟ يمكنك تعديل العدد لاحقاً لكن كل
          جهاز إضافي يُحتسب الآن.
        </p>
      </div>

      {/* Info card — plan's included allowance */}
      <Card padding={20} style={{ marginBottom: 20, background: BRAND.mint, border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconChip color={BRAND.greenDark}>
            <Icon.Shield size={18} />
          </IconChip>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.greenDark, marginBottom: 2 }}>
              باقة {plan?.nameAr || plan?.name || '—'} تشمل {includedDevices} جهاز مجاناً
            </div>
            <div style={{ fontSize: 13, color: BRAND.greenDark, opacity: 0.85 }}>
              كل جهاز إضافي بـ <b>{devicePrice.toLocaleString()} ر.س</b> — دفعة واحدة
              فقط (غير متكرر).
            </div>
          </div>
        </div>
      </Card>

      {/* Stepper */}
      <Card padding={28} style={{ marginBottom: 20 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: BRAND.muted, marginBottom: 16 }}>
            عدد الأجهزة الإضافية (بعد {includedDevices} المشمولة)
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24,
          }}
          >
            <StepperButton disabled={extraDevices <= 0} onClick={dec} ariaLabel="إنقاص">
              <MinusIcon />
            </StepperButton>
            <div style={{
              minWidth: 70, fontSize: 44, fontWeight: 800, color: BRAND.text,
              fontVariantNumeric: 'tabular-nums',
            }}
            >
              {extraDevices}
            </div>
            <StepperButton disabled={extraDevices >= MAX_EXTRA} onClick={inc} ariaLabel="زيادة">
              <Icon.Plus size={18} />
            </StepperButton>
          </div>
          <div style={{
            marginTop: 20, paddingTop: 20, borderTop: `1px solid ${BRAND.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
          >
            <span style={{ fontSize: 13.5, color: BRAND.muted }}>مجموع الأجهزة</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: BRAND.text, fontVariantNumeric: 'tabular-nums' }}>
              {totalDevices}
            </span>
          </div>
        </div>
      </Card>

      {/* Live cost preview */}
      <Card padding={20} style={{ background: BRAND.surfaceAlt, border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, color: BRAND.muted, marginBottom: 2 }}>
              رسوم الأجهزة الإضافية
            </div>
            <div style={{ fontSize: 11.5, color: BRAND.muted }}>
              {extraDevices} × {devicePrice.toLocaleString()} ر.س
            </div>
          </div>
          <div style={{
            fontSize: 22, fontWeight: 800, color: extraCost > 0 ? BRAND.greenDark : BRAND.muted,
            fontVariantNumeric: 'tabular-nums',
          }}
          >
            {extraCost.toLocaleString()} ر.س
          </div>
        </div>
        <div style={{
          marginTop: 12, fontSize: 12, color: BRAND.muted, lineHeight: 1.6,
        }}
        >
          ستظهر هذه التكلفة ضمن ملخص الدفع في الصفحة التالية، وتُضاف إلى المبلغ الكلي.
        </div>
      </Card>
    </Frame>
  );
}

// ─── Local helpers ─────────────────────────────────────

function StepperButton({ children, onClick, disabled, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        width: 48, height: 48, borderRadius: 24,
        border: `1.5px solid ${disabled ? BRAND.border : BRAND.green}`,
        background: disabled ? BRAND.surfaceAlt : '#fff',
        color: disabled ? BRAND.muted : BRAND.greenDark,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .15s', fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function MinusIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 10h12" />
    </svg>
  );
}
